import React, { useState, useEffect, useRef } from 'react';
import { X, Bell } from 'lucide-react';
import { useRbac } from '../context';
import {
  getNotifications,
  markNotificationAsRead,
  Notification,
} from '../services/notificationApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { formatRelativeTime } from './utils/formatRelativeTime';

/**
 * NotificationPopup Component
 * Shows all new unread notifications as popups to users
 */
const AdminNotificationPopup: React.FC = () => {
  const { user } = useRbac();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [needsScrolling, setNeedsScrolling] = useState(false);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  // Track which notifications have been shown to avoid showing them again
  const shownNotificationIds = useRef<Set<string>>(new Set());

  // Fetch all unread notifications for auto-popup
  const fetchUnreadNotifications = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const response = await getNotifications({ page: 1, limit: 50, unreadOnly: true });
      
      // Show every unread notification that hasn't been shown yet
      const unreadNotifications = response.notifications.filter(
        n => !shownNotificationIds.current.has(n._id)
      );

      // Sort by creation date (newest first)
      const sortedNotifications = unreadNotifications.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      if (sortedNotifications.length > 0) {
        // Mark these notifications as shown
        sortedNotifications.forEach(n => shownNotificationIds.current.add(n._id));
        
        setNotifications(sortedNotifications);
        setCurrentIndex(0);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Error fetching unread notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check for new unread notifications on mount and periodically
  useEffect(() => {
    if (!user) return;

    // Initial check after a short delay to avoid showing notifications immediately on page load
    const initialTimeout = setTimeout(() => {
      fetchUnreadNotifications();
    }, 2000); // 2 second delay on initial load

    // Check every 15 seconds for new notifications
    const interval = setInterval(fetchUnreadNotifications, 15000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle scroll to check if user reached bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 10; // 10px threshold
    setHasScrolledToBottom(isAtBottom);
  };

  // Reset scroll state when notification changes and check if scrolling is needed
  useEffect(() => {
    if (notifications.length > 0 && isOpen) {
      setHasScrolledToBottom(false);
      
      // Use requestAnimationFrame and multiple checks to ensure DOM is fully rendered
      const checkScrolling = () => {
        const container = messageContainerRef.current;
        if (container) {
          // Reset scroll position
          container.scrollTop = 0;
          
          // Check if content needs scrolling (with a small threshold to account for rounding)
          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;
          const contentNeedsScrolling = scrollHeight > clientHeight + 2; // 2px threshold
          
          setNeedsScrolling(contentNeedsScrolling);
          
          // If content doesn't need scrolling, automatically set hasScrolledToBottom to true
          if (!contentNeedsScrolling) {
            setHasScrolledToBottom(true);
          }
        }
      };
      
      // Check using requestAnimationFrame for next paint, then with delays
      requestAnimationFrame(() => {
        checkScrolling();
        setTimeout(checkScrolling, 50);
        setTimeout(checkScrolling, 150);
        setTimeout(checkScrolling, 300);
      });
    }
  }, [currentIndex, notifications, isOpen]);

  // Mark current notification as read and move to next
  const handleClose = async () => {
    if (notifications.length === 0) {
      setIsOpen(false);
      return;
    }

    const currentNotification = notifications[currentIndex];
    
    try {
      await markNotificationAsRead(currentNotification._id);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }

    // Move to next notification or close
    if (currentIndex < notifications.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setHasScrolledToBottom(false);
    } else {
      setIsOpen(false);
      setNotifications([]);
      setCurrentIndex(0);
      setHasScrolledToBottom(false);
      // Fetch new notifications after closing
      setTimeout(() => {
        fetchUnreadNotifications();
      }, 1000);
    }
  };

  // Close all notifications
  const handleCloseAll = async () => {
    // Mark all remaining as read
    const remainingNotifications = notifications.slice(currentIndex);
    try {
      await Promise.all(
        remainingNotifications.map(n => markNotificationAsRead(n._id))
      );
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }

    setIsOpen(false);
    setNotifications([]);
    setCurrentIndex(0);
    // Fetch new notifications after closing all
    setTimeout(() => {
      fetchUnreadNotifications();
    }, 1000);
  };

  if (!user || notifications.length === 0 || !isOpen) {
    return null;
  }

  const currentNotification = notifications[currentIndex];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Prevent closing by clicking outside or pressing ESC
      if (!open && hasScrolledToBottom) {
        setIsOpen(false);
      }
    }}>
      <DialogContent 
        className="sm:max-w-lg max-h-[90vh] flex flex-col [&>button]:hidden border-0 outline-none ring-0 focus:ring-0 focus:outline-none"
        onAnimationEnd={() => {
          // Check scrolling after dialog animation completes
          if (messageContainerRef.current) {
            const container = messageContainerRef.current;
            container.scrollTop = 0;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;
            const contentNeedsScrolling = scrollHeight > clientHeight + 2;
            setNeedsScrolling(contentNeedsScrolling);
            if (!contentNeedsScrolling) {
              setHasScrolledToBottom(true);
            }
          }
        }}
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bell className="h-6 w-6 text-orange-600" />
            <DialogTitle className="text-xl font-semibold">
              {currentNotification.title}
            </DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4 min-h-0 pr-2">
          <div 
            ref={messageContainerRef}
            className="max-h-[50vh] overflow-y-auto"
            onScroll={handleScroll}
          >
            <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
              {currentNotification.message}
            </p>
          </div>
          
          {needsScrolling && !hasScrolledToBottom && (
            <div className="text-xs text-orange-600 text-center py-1">
              ↓ Scroll to the bottom to continue
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t flex-shrink-0">
            <span>{formatRelativeTime(new Date(currentNotification.createdAt))}</span>
            <span>by {currentNotification.createdBy.username}</span>
          </div>

          {notifications.length > 1 && (
            <div className="text-sm text-gray-500 text-center flex-shrink-0">
              {currentIndex + 1} of {notifications.length} notifications
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 flex-shrink-0 border-t mt-2">
          {notifications.length > 1 && (
            <Button
              variant="outline"
              onClick={handleCloseAll}
              className="text-base"
              disabled={!hasScrolledToBottom}
            >
              Close All
            </Button>
          )}
          <Button
            onClick={handleClose}
            disabled={!hasScrolledToBottom}
            className="bg-orange-600 hover:bg-orange-700 text-white text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentIndex < notifications.length - 1 ? 'Next' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminNotificationPopup;

